'use client';

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@svs/ui/react/button';
import ArrowLeft from '@svs/ui/react/icons/ArrowLeft';
import ArrowRight from '@svs/ui/react/icons/ArrowRight';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../utils/entry.js';
/**
 * Wrapper that adds horizontal scroll area with buttons for a scroll container ref.
 *
 * @param root0 component props
 * @param root0.scrollElementRef ref to the horizontally scrollable element
 * @param root0.children wrapped content
 * @param root0.className wrapper class name
 * @param root0.navigationClassName class name for the navigation buttons container
 * @param root0.leftLabel aria label for left button
 * @param root0.rightLabel aria label for right button
 * @param root0.scrollAmount optional explicit scroll amount in pixels
 * @returns wrapped content with horizontal scroll area
 */
export default function HorizontalScrollArea({
  scrollElementRef,
  children,
  className,
  navigationClassName,
  leftLabel = 'Skrolla vänster',
  rightLabel = 'Skrolla höger',
  scrollAmount
}) {
  const wrapperRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [arrowTop, setArrowTop] = useState(undefined);
  const arrowRafId = useRef(undefined);
  const lastArrowTop = useRef(undefined);
  const updateArrowPosition = useCallback(() => {
    if (arrowRafId.current !== undefined) return;
    arrowRafId.current = requestAnimationFrame(() => {
      arrowRafId.current = undefined;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const visibleTop = Math.max(rect.top, 0);
      const visibleBottom = Math.min(rect.bottom, viewportHeight);
      if (visibleBottom <= visibleTop) return;
      const visibleCenterRelative = (visibleTop + visibleBottom) / 2 - rect.top;
      const clamped = Math.round(Math.max(20, Math.min(visibleCenterRelative, rect.height - 20)));
      if (clamped !== lastArrowTop.current) {
        lastArrowTop.current = clamped;
        setArrowTop(clamped);
      }
    });
  }, []);
  useLayoutEffect(() => {
    updateArrowPosition();
    window.addEventListener('scroll', updateArrowPosition, {
      passive: true
    });
    window.addEventListener('resize', updateArrowPosition, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', updateArrowPosition);
      window.removeEventListener('resize', updateArrowPosition);
      if (arrowRafId.current !== undefined) {
        cancelAnimationFrame(arrowRafId.current);
        arrowRafId.current = undefined;
      }
    };
  }, [updateArrowPosition]);
  const updateScrollButtonVisibility = useCallback(() => {
    const scrollElement = scrollElementRef.current;
    if (!scrollElement) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    setCanScrollLeft(scrollElement.scrollLeft > 0);
    setCanScrollRight(scrollElement.scrollLeft + scrollElement.clientWidth < scrollElement.scrollWidth - 1);
  }, [scrollElementRef]);
  useLayoutEffect(() => {
    const scrollElement_0 = scrollElementRef.current;
    let rafId;
    let resizeObserver;
    const handleScroll = () => {
      updateScrollButtonVisibility();
    };
    if (scrollElement_0) {
      rafId = requestAnimationFrame(() => {
        updateScrollButtonVisibility();
      });
      scrollElement_0.addEventListener('scroll', handleScroll, {
        passive: true
      });
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          updateScrollButtonVisibility();
        });
        resizeObserver.observe(scrollElement_0);
      }
    }
    return () => {
      if (scrollElement_0) {
        scrollElement_0.removeEventListener('scroll', handleScroll);
      }
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver?.disconnect();
    };
  }, [scrollElementRef, updateScrollButtonVisibility]);
  const scrollHorizontally = useCallback(direction => {
    const scrollElement_1 = scrollElementRef.current;
    if (!scrollElement_1) {
      return;
    }
    const amount = scrollAmount ?? Math.max(scrollElement_1.clientWidth * 0.8, 120);
    const delta = direction === 'left' ? -amount : amount;
    scrollElement_1.scrollBy({
      left: delta,
      behavior: 'smooth'
    });
  }, [scrollAmount, scrollElementRef]);
  return _jsxs("div", {
    ref: wrapperRef,
    className: cn('relative h-full', className),
    children: [children, _jsxs("div", {
      className: cn('pointer-events-none absolute right-px left-px z-10 flex items-center justify-between', navigationClassName),
      style: {
        top: arrowTop ?? '50%',
        transform: 'translateY(-50%)'
      },
      children: [_jsx("div", {
        children: canScrollLeft ? _jsx(Button, {
          type: "button",
          variant: "primary",
          size: "md",
          className: "pointer-events-auto rounded-full",
          onClick: () => {
            scrollHorizontally('left');
          },
          "aria-label": leftLabel,
          children: _jsx(ArrowLeft, {
            className: "h-4 w-4"
          })
        }) : undefined
      }), _jsx("div", {
        children: canScrollRight ? _jsx(Button, {
          type: "button",
          variant: "primary",
          size: "md",
          className: "pointer-events-auto rounded-full",
          onClick: () => {
            scrollHorizontally('right');
          },
          "aria-label": rightLabel,
          children: _jsx(ArrowRight, {
            className: "h-4 w-4"
          })
        }) : undefined
      })]
    })]
  });
}